import { hasUnpricedItem } from '@klear/payment/item'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import toInteger from 'lodash/toInteger'
import { operationalSupplierIdAsNumber } from '@klear/operational'
import { operationalAmountLabel, operationalLineLabel } from '@klear/operational/presentation'
import { PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT } from '@klear/payment/constants'
import { isEventLine, isNavigableLine, type PaymentLineItem } from '@klear/payment/line-item'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import { useSuppliers } from '@klear/ui/hooks/use-suppliers'
import { Money } from '@klear/money'
import { payableTotalFromComponents } from '@klear/payable/total'
import { Card } from '@klear/ui/components/card'
import { Button } from '@klear/ui/components/button'
import { Pencil, X } from 'lucide-react'
import { useNavigateWithReturnUrl } from '@klear/ui/wizard/return-url'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import { OperationalSelectOrCreate } from '@/app/operational/workflow/OperationalSelectOrCreate'
import { PayableCostRows } from '@/app/payable/workflow/PayableCostRows'
import { coveredEventDetails, coveredLineMeta } from '@/app/payable/workflow/payable-covered-lines'
import { operationalCreatePath, operationalEditPath } from '@/app/operational/operational-wizard-page'
import type { PayableStepCtx } from '@/app/payable/workflow/PayableWizardContext'

export default function OperationalStep({ ctx }: WizardStepComponentProps<PayableStepCtx>) {
  const { traderId: traderIdParam } = useParams<{ traderId?: string }>()
  const traderId = toInteger(traderIdParam)
  const { navigateWithReturnUrl } = useNavigateWithReturnUrl()
  const {
    form,
    dirty,
    operationalLinkLocked,
    attachExistingOperational,
    removeOperational,
    setItems,
    goNext,
    previewResult,
    payable
  } = ctx
  const coveredIds = form.operationalEventIds

  const componentsDirty = dirty.items === true || dirty.operationalEventIds === true
  const serverPayable = previewResult ?? (componentsDirty ? null : payable)
  const linkedOperationals = serverPayable?.operationals ?? []
  const lineItems = serverPayable?.lineItems ?? []

  const { operationals: pickedOperationals, isLoading } = useOperationals(
    { orderEventId: coveredIds },
    { enabled: coveredIds.length > 0 }
  )
  const { suppliers } = useSuppliers(traderId)

  const rows = useMemo<PaymentLineItem[]>(() => {
    if (lineItems.length > 0) return lineItems
    return pickedOperationals.map((operational) => ({
      source: PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT,
      label: operationalLineLabel(operational),
      amount: operational.amount ?? undefined,
      orderEventId: operational.id ?? undefined,
      orderId: operational.orderId ?? undefined,
      parentEventId: operational.parentId ?? undefined,
      effectiveDate: operational.effectiveDate?.toString()
    })) as PaymentLineItem[]
  }, [lineItems, pickedOperationals])

  const detailByEventId = useMemo(() => coveredEventDetails(pickedOperationals), [pickedOperationals])

  const eventRows = rows.filter(isEventLine)
  const eventCount = eventRows.length
  const supplierId = form.supplierId ?? serverPayable?.supplierId ?? null
  const supplierName =
    (previewResult ?? payable)?.supplierName ?? suppliers.find((s) => s.id === supplierId)?.name ?? null
  const currency = serverPayable?.amount.currency ?? form.currency

  const total =
    serverPayable?.amount ??
    payableTotalFromComponents(
      [
        ...eventRows.map((row) => row.amount).filter((amount): amount is Money => amount != null),
        ...form.items.map((item) => new Money(item.amount))
      ],
      currency
    ).total
  const orderId = form.orderId ?? linkedOperationals[0]?.orderId ?? pickedOperationals[0]?.orderId ?? null

  const hasUnfilledItem = hasUnpricedItem(form.items)

  if (supplierId == null) {
    return <div className="text-sm text-muted-foreground">Choose a supplier first.</div>
  }

  const onCreateNew = () =>
    navigateWithReturnUrl(operationalCreatePath(traderId, null, { parentEventId: form.purchaseOrderId, supplierId }))

  const onEditOperational = (eventId?: number) => {
    if (eventId == null || eventId < 1) return
    const event = linkedOperationals.find((operational) => operational.id === eventId)
    const picked = pickedOperationals.find((operational) => operational.id === eventId)
    navigateWithReturnUrl(
      operationalEditPath(
        traderId,
        eventId,
        event?.orderId ?? picked?.orderId ?? orderId ?? 0,
        event?.parentEventId ?? picked?.parentId ?? form.purchaseOrderId
      )
    )
  }

  const canRemove = !operationalLinkLocked && (eventCount > 1 || form.items.length > 0)

  const strangers = pickedOperationals.filter(
    (operational) => operationalSupplierIdAsNumber(operational.supplierId) !== supplierId
  )

  return (
    <div className="space-y-6 w-full max-w-2xl">
      <Card className="p-6 space-y-4">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground">What this payment covers</h2>
          {supplierName ? (
            <span className="text-sm text-muted-foreground">
              Paying <span className="font-medium text-foreground">{supplierName}</span>
            </span>
          ) : null}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            Operational events{eventCount > 0 ? ` (${eventCount})` : ''}
          </h3>
          <p className="text-sm text-muted-foreground">
            Costs this supplier billed against a trade. Each stays tracked on its own trade, and its amount is owned by
            the event — open one to change what it is worth.
          </p>
          {eventRows.length > 0 ? (
            <div className="space-y-2 pt-1">
              {eventRows.map((item, index) => {
                const meta = coveredLineMeta(item, detailByEventId)
                return (
                  <div
                    key={item.orderEventId ?? `${item.source}-${index}`}
                    className="flex items-start justify-between gap-3 rounded-md border border-border p-3 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">{item.label}</div>
                      {meta.length > 0 ? (
                        <div className="tabular-nums text-muted-foreground">{meta.join(' · ')}</div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-medium tabular-nums">{operationalAmountLabel(item.amount)}</span>
                      {isNavigableLine(item) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEditOperational(item.orderEventId)}
                          aria-label={`Edit ${item.label}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {canRemove && isEventLine(item) && item.orderEventId != null ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOperational(item.orderEventId!)}
                          aria-label={`Remove ${item.label} from this payment`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : isLoading ? (
            <div className="text-sm text-muted-foreground">Loading operational events…</div>
          ) : (
            <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
              None yet — add one below.
            </p>
          )}
          {strangers.length > 0 ? (
            <p className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              {strangers.map((operational) => operationalLineLabel(operational)).join(', ')}{' '}
              {strangers.length === 1 ? 'is not billed by' : 'are not billed by'}{' '}
              {supplierName ?? 'the supplier this payment pays'}. A payment goes to one supplier, so open{' '}
              {strangers.length === 1 ? 'it' : 'each one'} and change the supplier, or go back and change who this
              payment pays.
            </p>
          ) : null}
        </div>
      </Card>

      {operationalLinkLocked ? null : (
        <OperationalSelectOrCreate
          supplierId={supplierId}
          currency={currency}
          excludeIds={coveredIds}
          onAttach={attachExistingOperational}
          onCreateNew={onCreateNew}
        />
      )}

      <Card className="p-6 space-y-4">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            Untracked line items{form.items.length > 0 ? ` (${form.items.length})` : ''}
          </h3>
          <p className="text-sm text-muted-foreground">
            Costs tied to no trade and no event — a wire fee, an FX spread, a correction. Klear does not track these
            against a trade; they exist only on this payment. A correction can be negative.
          </p>
          <PayableCostRows
            items={form.items}
            currency={currency}
            readOnly={operationalLinkLocked}
            onChange={setItems}
          />
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm text-muted-foreground">Total</span>
          <span className="text-base font-semibold tabular-nums">{total.toString()}</span>
        </div>
      </Card>

      {operationalLinkLocked || (eventCount === 0 && form.items.length === 0) ? null : (
        <div className="flex justify-end">
          <Button onClick={goNext} disabled={hasUnfilledItem || strangers.length > 0}>
            Continue to payment
          </Button>
        </div>
      )}
    </div>
  )
}
